import React from 'react';
import { Box } from '@cp/ds/src/components/box';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useCatalogItemIsResponded, useCatalogItemIsViewed } from '@cp/entities/client';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { ICatalogItem } from 'src/entities/catalog';
import { VacancyCard } from 'src/entities/vacancy';
import { ResponseButton as RS, RespondToVacancyDeps } from 'src/features/catalogItem/response';
import { useClientProfile } from 'src/entities/client';
import { useCheckPatronageCompleted, useShowPatronageHints } from 'src/features/client/current/patronage';
import { withSubscriptionTooltip } from 'src/features/billing';
import { CatalogEvents } from 'src/features/catalog';
import { useTrackEvent, withTrackClick } from 'src/shared/tracking';
import { useAuth, withAuth } from 'src/features/auth';
import { Button } from '@cp/ds/src/components/button';
import { withConnect } from 'src/features/catalogItem/connect';
import { _experimentalComponentBuilder as componentBuilder } from 'src/shared/componentBuilder';

const ResponseButtonBuilder = componentBuilder(RS).compose(withAuth).compose(withSubscriptionTooltip);

const ConnectButton = componentBuilder(Button)
  .compose(withConnect)
  .composeWithArgs(withTrackClick, CatalogEvents.ItemQuestion)
  .compose(withAuth)
  .buildWithProps({
    size: 'extraSmall',
    variant: 'secondary',
    color: 'gray ultra light',
  });

export const VacancyCardWidget = ({ url, ...props }: ICatalogItem) => {
  const theme = useTheme();
  const authCase = useAuth();
  const track = useTrackEvent();
  const isLoggedIn = useIsLoggedIn();
  const clientProfile = useClientProfile();
  const checkPatronageCompleted = useCheckPatronageCompleted();
  const showPatronageHints = useShowPatronageHints();
  const respondToVacancyDeps = React.useMemo(
    (): RespondToVacancyDeps => ({
      onNeedLogin: authCase,
      patronage: {
        check: checkPatronageCompleted,
        action: showPatronageHints,
      },
    }),
    [authCase, checkPatronageCompleted, showPatronageHints],
  );
  const isResponded = useCatalogItemIsResponded(props.data.id);
  const isViewed = useCatalogItemIsViewed(props.data.id);

  const handleTitleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (isLoggedIn) {
      e.preventDefault();
    }
  };

  const ResponseButton = ResponseButtonBuilder.buildWithProps({
    tooltipText: 'Чтобы откликаться и видеть контакты необходимо',
    request: { responder: clientProfile, catalogItem: props.data },
    onClickCapture: () => track(CatalogEvents.ItemResponse()),
    deps: respondToVacancyDeps,
    variant: 'secondary',
    size: 'extraSmall',
  });

  return (
    <VacancyCard
      disableInfo={!isLoggedIn}
      footer={
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1, flex: '1 0 auto', justifyContent: 'end' }}>
          <ResponseButton>Я могу</ResponseButton>
          <ConnectButton catalogItemId={props.data.id}>Задать вопрос</ConnectButton>
        </Box>
      }
      onTitleClick={handleTitleClick}
      replied={isResponded}
      sx={{ backgroundColor: theme.palette.background.default }}
      url={url ? String(url) : undefined}
      viewed={isViewed}
      {...props}
    />
  );
};
